import { type ReactNode, createContext, useContext, useState, useCallback, useMemo } from 'react'

interface ChatModalContextType {
  open: boolean
  requestId: string | undefined
  handleOpen: (requestId: string, onClose?: () => void) => void
  handleClose: () => void
}

const ChatModalContext = createContext<ChatModalContextType | undefined>(undefined)

export const useChatModalContext = () => {
  const context = useContext(ChatModalContext)
  if (!context) {
    throw new Error('useChatModalContext must be used within a ChatModalProvider')
  }
  return context
}

interface ChatModalProviderProps {
  children: ReactNode
}

export const ChatModalProvider = ({ children }: ChatModalProviderProps) => {
  const [open, setOpen] = useState(false)
  const [requestId, setRequestId] = useState<string | undefined>(undefined)
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>(undefined)

  const handleOpen = useCallback((newRequestId: string, callback?: () => void) => {
    setRequestId(newRequestId)
    setOnCloseCallback(() => callback)
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setRequestId(undefined)
    if (onCloseCallback) {
      onCloseCallback()
      setOnCloseCallback(undefined)
    }
  }, [onCloseCallback])

  const value: ChatModalContextType = useMemo(
    () => ({
      open,
      requestId,
      handleOpen,
      handleClose,
    }),
    [open, requestId, handleOpen, handleClose],
  )

  return <ChatModalContext.Provider value={value}>{children}</ChatModalContext.Provider>
}
